import type {
  GetAccessRoleRequestParams,
  GetAccessRoleResponse,
  GetAccessRolesRequestParams,
  GetRolePermissionsRequestParams,
  GetRolePermissionsResponse,
  GetUsersByRoleRequestParams,
  GetUsersByRoleResponse,
  GetUserRoleAssignmentsRequestParams,
  GetUserRoleAssignmentsResponse,
  GetUserRoleAssignmentRequestParams,
  GetUserRoleAssignmentResponse,
  CreateNewAccessRoleResponse,
  CreateNewAccessRoleRequestParams,
  AddRolePermissionsRequestParams,
  AddRolePermissionsResponse,
  AddUserRoleAssignmentRequestParams,
  AddUserRoleAssignmentResponse,
  ChangeAccessRoleNameRequestParams,
  ChangeAccessRoleNameResponse,
  DeleteAccessRoleRequestParams,
  DeleteAccessRoleResponse,
  RemoveRolePermissionsRequestParams,
  RemoveRolePermissionsResponse,
  RemoveUserRoleAssignmentResponse,
  RemoveUserRoleAssignmentRequestParams,
  GetAccessControlAccessRolesResponse
} from '@gat-ship/types'
import { useGsCloudBackend } from '@gat-ship/http-client'

export const ACCESS_CONTROL_API_PATHS = {
  getAccessRoles: (params?: GetAccessRolesRequestParams) =>
    `access-control/access-roles${params?.includeSoftDeleted ? `?include_soft_deleted=${params.includeSoftDeleted}` : ''}`,
  getAccessRole: (params: GetAccessRoleRequestParams) =>
    `access-control/access-roles/${params.roleId}`,
  getRolePermissions: (params: GetRolePermissionsRequestParams) =>
    `access-control/access-roles/${params.roleId}/permissions`,
  getUsersByRole: (params: GetUsersByRoleRequestParams) =>
    `access-control/access-roles/${params.roleId}/users`,
  getUserRoleAssignments: (params: GetUserRoleAssignmentsRequestParams) =>
    `access-control/users/${params.userId}/role-assignments`,
  getUserRoleAssignment: (params: GetUserRoleAssignmentRequestParams) =>
    `access-control/users/${params.userId}/role-assignments/${params.roleAssignmentId}`,
  createNewAccessRole: `access-control/access-roles`,
  addRolePermissions: (params: AddRolePermissionsRequestParams) =>
    `access-control/access-roles/${params.roleId}/permissions`,
  addUserRoleAssignment: (params: AddUserRoleAssignmentRequestParams) =>
    `access-control/users/${params.userId}/role-assignments`,
  changeAccessRoleName: (params: ChangeAccessRoleNameRequestParams) =>
    `access-control/access-roles/${params.roleId}/change-name`,
  deleteAccessRole: (params: DeleteAccessRoleRequestParams) =>
    `access-control/access-roles/${params.roleId}`,
  removeRolePermissions: (params: RemoveRolePermissionsRequestParams) =>
    `/access-control/access-roles/${params.roleId}/permissions`,
  removeUserRoleAssignment: (params: RemoveUserRoleAssignmentRequestParams) =>
    `access-control/users/${params.userId}/role-assignments/${params.roleAssignmentId}`
}

export function useAccessControlApiCalls() {
  const { apiGet, apiPost, apiPut, apiDelete } = useGsCloudBackend()

  /** Gets all access roles defined in the database, with an option to including soft deleted roles. */
  async function getAccessRolesRequest(params?: GetAccessRolesRequestParams) {
    return await apiGet<GetAccessControlAccessRolesResponse>(
      ACCESS_CONTROL_API_PATHS.getAccessRoles(params)
    )
  }

  /** Gets a full definition of a single defined access role including permissions. */
  async function getAccessRoleRequest(params: GetAccessRoleRequestParams) {
    return await apiGet<GetAccessRoleResponse>(
      ACCESS_CONTROL_API_PATHS.getAccessRole(params)
    )
  }

  /** Gets a list of all permissions assigned to a single defined access role. */
  async function getRolePermissionsRequest(
    params: GetRolePermissionsRequestParams
  ) {
    return await apiGet<GetRolePermissionsResponse>(
      ACCESS_CONTROL_API_PATHS.getRolePermissions(params)
    )
  }

  /** Gets a list of all users assigned to a single defined access role. */
  async function getUsersByRoleRequest(params: GetUsersByRoleRequestParams) {
    return await apiGet<GetUsersByRoleResponse>(
      ACCESS_CONTROL_API_PATHS.getUsersByRole(params)
    )
  }

  /** Get all user role assignments for a specific user. */
  async function getUserRoleAssignmentsRequest(
    params: GetUserRoleAssignmentsRequestParams
  ) {
    return await apiGet<GetUserRoleAssignmentsResponse>(
      ACCESS_CONTROL_API_PATHS.getUserRoleAssignments(params)
    )
  }

  /** Get a single user role assignment for a specific user. */
  async function getUserRoleAssignmentRequest(
    params: GetUserRoleAssignmentRequestParams
  ) {
    return await apiGet<GetUserRoleAssignmentResponse>(
      ACCESS_CONTROL_API_PATHS.getUserRoleAssignment(params)
    )
  }

  /** Creates a new customer defined access role. */
  async function createNewAccessRoleRequest(
    params: CreateNewAccessRoleRequestParams
  ) {
    return await apiPost<CreateNewAccessRoleResponse>(
      ACCESS_CONTROL_API_PATHS.createNewAccessRole,
      params.payload,
      params.commandId
    )
  }

  /**
   * Adds a list of permissions to a single defined access role.
   * Normally, the permissionCodes array contains a single entry because users add permissions one by one.
   * To accommodate a future UI that might bulk-hide permissions (consolidate into one logical permission),
   * we permit up to 20 codes per request — but no more, as that would go beyond the endpoint’s design.
   */
  async function addRolePermissionsRequest(
    params: AddRolePermissionsRequestParams
  ) {
    return await apiPost<AddRolePermissionsResponse>(
      ACCESS_CONTROL_API_PATHS.addRolePermissions(params),
      params.payload,
      params.commandId
    )
  }

  /** Add a new user role assignment for a specific user. */
  async function addUserRoleAssignmentRequest(
    params: AddUserRoleAssignmentRequestParams
  ) {
    return await apiPost<AddUserRoleAssignmentResponse>(
      ACCESS_CONTROL_API_PATHS.addUserRoleAssignment(params),
      params.payload,
      params.commandId
    )
  }

  /** Changes the name and description of a customer-defined access role. */
  async function changeAccessRoleNameRequest(
    params: ChangeAccessRoleNameRequestParams
  ) {
    return await apiPut<ChangeAccessRoleNameResponse>(
      ACCESS_CONTROL_API_PATHS.changeAccessRoleName(params),
      params.payload,
      params.commandId
    )
  }

  /** Deletes a customer defined access role. */
  async function deleteAccessRoleRequest(
    params: DeleteAccessRoleRequestParams
  ) {
    return await apiDelete<DeleteAccessRoleResponse>(
      ACCESS_CONTROL_API_PATHS.deleteAccessRole(params),
      undefined,
      params.commandId
    )
  }

  /** Removes a list of permissions from a single defined access role.
   *
   * Normally, the permissionCodes array contains a single entry because users remove permissions one by one.
   * To accommodate a future UI that might bulk-hide permissions (consolidate into one logical permission),
   * we permit up to 20 codes per request — but no more, as that would go beyond the endpoint’s design.
   */
  async function removeRolePermissionsRequest(
    params: RemoveRolePermissionsRequestParams
  ) {
    return await apiDelete<RemoveRolePermissionsResponse>(
      ACCESS_CONTROL_API_PATHS.removeRolePermissions(params),
      params.payload,
      params.commandId
    )
  }

  /** Removes a single user role assignment for a specific user. */
  async function removeUserRoleAssignmentRequest(
    params: RemoveUserRoleAssignmentRequestParams
  ) {
    return await apiDelete<RemoveUserRoleAssignmentResponse>(
      ACCESS_CONTROL_API_PATHS.removeUserRoleAssignment(params),
      undefined,
      params.commandId
    )
  }

  return {
    getAccessRolesRequest,
    getAccessRoleRequest,
    getRolePermissionsRequest,
    getUsersByRoleRequest,
    getUserRoleAssignmentsRequest,
    getUserRoleAssignmentRequest,
    createNewAccessRoleRequest,
    addRolePermissionsRequest,
    addUserRoleAssignmentRequest,
    changeAccessRoleNameRequest,
    deleteAccessRoleRequest,
    removeRolePermissionsRequest,
    removeUserRoleAssignmentRequest
  }
}
